import { useEffect, useRef, useState } from "react";
import { controllerNudge } from "./game/controller-nudge";
import { Game, type HudState } from "./game/Game";
import { touchOnly } from "./game/input";
import { storage } from "./game/storage";
import { HILL_KEYS, TouchPad } from "./game/touch";
import { TouchTutorial, markTutorialSeen, tutorialSeen } from "./game/touch-tutorial";
import { DEFAULT_MAP_ID, MAP_CATALOG } from "./game/maps";
import { HILL_SITE } from "./game/site";
import { defaultSettings, remapped } from "./game/settings";
import { DEFAULT_SKATER_ID } from "./game/skaters";
import { DEFAULT_STATS } from "./game/stats";
import { ControlsHelp } from "./ui/ControlsHelp";
import { DemoBadge } from "./ui/DemoBadge";
import { Hud } from "./ui/Hud";
import { LoadingBadge } from "./ui/LoadingBadge";
import { PauseOverlay, pauseOverlayProps } from "./ui/PauseOverlay";
import { ChallengeCard } from "./ui/ChallengeCard";
import { HubList } from "./ui/HubList";
import { RunResults } from "./ui/RunResults";
import { RotateCard } from "./ui/RotateCard";
import { HillHud } from "./ui/HillHud";
import { Lobby } from "./ui/Lobby";
import { useNotesSeen, WhatsNewDialog } from "./ui/WhatsNew";
import { QuickSettings } from "./ui/QuickSettings";
import { TopButtons } from "./ui/TopButtons";
import { TouchControls } from "./ui/TouchControls";
import { showTouchControls } from "./ui/touch-visibility";
import { useFullscreen } from "./ui/use-fullscreen";
import { usePhoneScreen } from "./ui/use-phone-screen";
import { activeInput } from "./ui/input-names";

const EMPTY_HUD: HudState = {
  score: 0,
  bestCombo: 0,
  bestComboText: "",
  recentCombos: [],
  special: 0,
  specialFull: false,
  comboText: "",
  comboBase: 0,
  comboMult: 0,
  balance: null,
  manualBalance: null,
  runOut: null,
  packItem: null,
  packKeys: null,
  ride: "board",
  rideHint: null,
  message: null,
  padName: null,
  padSeen: true,
  padBlocked: false,
  stance: null,
  ollieCharge: 0,
  grindReady: false,
  revertReady: false,
  source: "keyboard",
  speed: 0,
  paused: false,
  pauseIndex: 0,
  pauseScreen: "main",
  mapId: DEFAULT_MAP_ID,
  loading: null,
  gaps: [],
  demo: null,
  run: null,
  goals: [],
  challenge: null,
  prompt: null,
  hub: null,
  hasHubs: false,
  guide: null,
  pauseHubs: [],
  applying: false,
  settings: { settings: defaultSettings(), rebinding: null },
  stats: { stats: DEFAULT_STATS, base: DEFAULT_STATS, points: 0 },
  skater: DEFAULT_SKATER_ID,
  hill: null,
};

export function App() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const gameRef = useRef<Game | null>(null);
  const [hud, setHud] = useState<HudState>(EMPTY_HUD);
  const [showHelp, setShowHelp] = useState(false);
  // Release notes (#159): a dot until the newest day is seen; the start screen's link opens them.
  const [newNotes, markNotesSeen] = useNotesSeen();
  const [showNews, setShowNews] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [started, setStarted] = useState(false);
  // A phone or tablet gets touch wording and the on-screen stick and buttons.
  const [touch] = useState(touchOnly);
  const phone = usePhoneScreen(touch);
  // A laptop with a touchscreen: a finger on the park brings the controls up under Auto.
  const [touchSeen, setTouchSeen] = useState(false);
  const [touchPad] = useState(() => new TouchPad());
  // The first time the touch controls come up, a 20s walk through them (null once seen or skipped).
  const [tutorial, setTutorial] = useState(() => (tutorialSeen(storage()) ? null : new TouchTutorial()));
  // The start screen's controller line (#161), closed for this page load.
  const [nudgeClosed, setNudgeClosed] = useState(false);
  const nudge = (where: "start" | "controls") =>
    controllerNudge({ where, padSeen: hud.padSeen, touch, tutorialSeen: tutorial === null, dismissed: nudgeClosed });
  const tutorialDone = () => {
    markTutorialSeen(storage());
    setTutorial(null);
  };

  // The skater waits behind the start screen instead of rolling off unwatched.
  useEffect(() => {
    if (started) gameRef.current?.begin();
  }, [started]);

  const watchDemo = () => {
    gameRef.current?.startDemo();
    setStarted(true);
  };

  /** Out of the demo and back to the start screen (maps, run, demo again). */
  const stopDemo = () => {
    gameRef.current?.stopDemo();
    setStarted(false);
  };

  const startRun = () => {
    gameRef.current?.startRun();
    setStarted(true);
  };

  const fullscreen = useFullscreen();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    let game: Game;
    try {
      game = new Game(canvas, { onHud: setHud, onToggleHelp: () => setShowHelp((v) => !v), onLobby: () => setStarted(false), touch: touchPad });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      return;
    }
    gameRef.current = game;
    game.start();
    return () => {
      gameRef.current = null;
      game.dispose();
    };
  }, [touchPad]);

  /** Off the start screen on a phone: fullscreen where it's allowed, it's a game. */
  const skate = () => {
    if (touch) fullscreen.enter();
    else window.focus();
    setStarted(true);
  };

  const touchSettings = hud.settings.settings.touch;
  const touchControls = showTouchControls(hud, touch || touchSeen, started, touchSettings.mode);
  // Skating on glass (a touch device, controls not Off, and no pad unless they're On): the ? panel names the touch buttons.
  const onGlass = (touch || touchSeen) && touchSettings.mode !== "off" && (touchSettings.mode === "on" || !hud.padName);
  // The help and the hints name only this input (#147), switching live with a key, a pad button or a touch.
  const input = activeInput(hud.source, onGlass);
  // Riding the hill on a phone (#198): its own small HUD, its keys only, and the icon row folded into Pause.
  const phoneHill = phone && hud.hill !== null && !hud.demo;

  return (
    <main className="fixed inset-0 overflow-hidden bg-aqua-soft">
      <canvas
        ref={canvasRef}
        className="block h-full w-full"
        aria-label={HILL_SITE ? "Bomb the Hill" : "Skatepark"}
        // In the demo a tap (or click) on the park skips to the next line.
        onPointerDown={(e) => {
          if (e.pointerType === "touch") setTouchSeen(true);
          if (hud.demo) gameRef.current?.nextDemoLine();
        }}
      />
      {error ? (
        <div role="alert" className="absolute inset-0 grid place-items-center p-6 text-center font-bold text-ink">
          This game needs WebGL, and your browser could not start it: {error}
        </div>
      ) : (
        <>
          {/* Hidden behind the lobby (#225): its panels are the screen until you drop in. */}
          <div className={started ? "contents" : "invisible"}>
            <Hud hud={hud} touch={touch} inputToast={started} mirror={onGlass && touchSettings.left} input={input} />
          </div>
          {/* Not behind the lobby: the hill site (#219) opens on the hill. */}
          {hud.hill && started && !hud.paused && (
            <HillHud
              hill={hud.hill}
              compact={phone}
              input={input}
              onPick={(dir) => gameRef.current?.hillPick(dir)}
              onDropIn={() => gameRef.current?.hillDropIn()}
              onSkipLesson={() => gameRef.current?.skipHillTutorial()}
              onLobby={() => gameRef.current?.toLobby()}
            />
          )}
          {hud.loading && <LoadingBadge name={hud.loading} />}
          {hud.demo && <DemoBadge line={hud.demo} touch={touch} onStop={stopDemo} />}
          {touchControls && (
            <TouchControls
              pad={touchPad}
              nollie={hud.stance?.includes("Nollie") ?? false}
              settings={touchSettings}
              glow={hud}
              // The walkthrough is for a park's keys (#198): it waits for one.
              tutorial={hud.hill ? null : tutorial}
              onTutorialDone={tutorialDone}
              only={hud.hill ? HILL_KEYS : undefined}
            />
          )}
          <TopButtons
            touch={touch}
            paused={hud.paused}
            onPause={() => gameRef.current?.openPause()}
            onToggleHelp={() => setShowHelp((v) => !v)}
            fullscreen={fullscreen.full}
            onFullscreen={fullscreen.toggle}
            folded={phoneHill}
          >
            {/* Not over the first-time touch walkthrough's card either: on a phone the row would reach it. */}
            {started && !hud.demo && !hud.paused && !(touchControls && tutorial && !hud.hill) && (
              <QuickSettings
                settings={hud.settings.settings}
                applying={hud.applying}
                onPick={(key, value) => gameRef.current?.pickQuick(key, value)}
              />
            )}
          </TopButtons>
          {hud.paused && (
            <PauseOverlay
              {...pauseOverlayProps(hud)}
              input={input}
              newNotes={newNotes}
              onNotesSeen={markNotesSeen}
              onPick={(action) => gameRef.current?.runPauseAction(action)}
              onHover={(i) => gameRef.current?.setPauseIndex(i)}
            />
          )}
          {hud.run?.phase === "done" && !hud.paused && (
            <RunResults
              run={hud.run}
              goals={hud.goals}
              mapName={MAP_CATALOG.find((m) => m.id === hud.mapId)?.name ?? ""}
              onRetry={() => gameRef.current?.startRun()}
              onFreeSkate={() => gameRef.current?.endRun()}
              input={input}
            />
          )}
          {hud.challenge && hud.challenge.phase !== "running" && hud.challenge.phase !== "overtime" && !hud.paused && (
            <ChallengeCard
              challenge={hud.challenge}
              onGo={() => gameRef.current?.beginChallenge()}
              onRetry={() => gameRef.current?.retryChallenge()}
              onLeave={() => gameRef.current?.endChallenge()}
              onHub={hud.hasHubs ? () => gameRef.current?.backToHub() : undefined}
              input={input}
            />
          )}
          {hud.hub && !hud.paused && (
            <HubList
              hub={hud.hub}
              onPick={(id) => gameRef.current?.pickHubChallenge(id)}
              onHover={(i) => gameRef.current?.setHubIndex(i)}
              onClose={() => gameRef.current?.closeHub()}
              input={input}
            />
          )}
          {showHelp && <ControlsHelp onClose={() => setShowHelp(false)} remapped={remapped(hud.settings.settings)} input={input} touchDevice={touch || touchSeen} specials={hud.settings.settings.specials} ride={hud.ride} padTip={nudge("controls")} />}
          {!started && (
            <Lobby
              touch={touch}
              pad={hud.padName !== null}
              covered={showNews}
              rideId={hud.settings.settings.ride}
              onPickRide={(id) => gameRef.current?.pickRide(id)}
              mapId={hud.mapId}
              skaterId={hud.skater}
              onSkate={skate}
              onPickSkater={(id) => gameRef.current?.selectSkater(id)}
              onPickMap={(id) => void gameRef.current?.loadMap(id)}
              onLoadFile={(file) => gameRef.current?.loadMapFile(file)}
              onHillTutorial={() => gameRef.current?.startHillTutorial()}
              onWatchDemo={watchDemo}
              onStartRun={startRun}
              newNotes={newNotes}
              nudge={nudge("start")}
              onCloseNudge={() => setNudgeClosed(true)}
              onWhatsNew={() => {
                markNotesSeen();
                setShowNews(true);
              }}
            />
          )}
          {!started && showNews && <WhatsNewDialog onClose={() => setShowNews(false)} />}
          {/* Bomb the Hill plays upright too (#213), and the lobby fits a tall screen: only the parks ask to turn. */}
          {touch && started && !hud.hill && !hud.loading && <RotateCard />}
        </>
      )}
    </main>
  );
}
